import { useCallback, useLayoutEffect, useRef } from 'react';
import { MotionValue, m, useMotionValue, useScroll, useTransform } from 'framer-motion';
// @mui
import { Box } from '@mui/material';
//
import { CONTAINER, STAGE, TYPE } from '../../../theme/stage';
import { ANCHOR, HERO } from '../copy';
import { EASE_FN } from '../motion';
import ActionLink from '../primitives/ActionLink';
import { entrance, liftIn } from '../primitives/entrance';
import Rich from '../primitives/Rich';
import TwoTone from '../primitives/TwoTone';
import { dropFor, partingFor, segment } from '../stageMath';
import WordmarkDisplay from './WordmarkDisplay';

// ----------------------------------------------------------------------

export type HeroMode = 'pinned' | 'flow' | 'static';

type Props = {
  mode: HeroMode;
};

type Track<T = number> = {
  input: number[];
  output: T[];
  ease?: (t: number) => number;
};

type Tracks = Record<'copyO' | 'copyY' | 'oScale' | 'oTurn' | 'liftO' | 'fadeO', Track> & {
  face: Track<string>;
};

function hold<T>(value: T): Track<T> {
  return { input: [0, 1], output: [value, value] };
}

const MAPS: Record<HeroMode, Tracks> = {
  pinned: {
    copyO: { input: [0, 0.3], output: [1, 0] },
    copyY: { input: [0, 0.3], output: [0, -40] },
    face: {
      input: [0.2, 0.8, 1],
      output: [STAGE.ink, STAGE.inkRecede, STAGE.ink2],
      ease: EASE_FN.inOutCubic,
    },
    oScale: { input: [0.2, 0.8], output: [0.8621, 1], ease: EASE_FN.inOutCubic },
    oTurn: { input: [0.2, 0.8], output: [0, 30], ease: EASE_FN.inOutCubic },
    liftO: { input: [0.2, 0.8], output: [0, 1], ease: EASE_FN.inOutCubic },
    fadeO: { input: [0.86, 1], output: [0, 0.8] },
  },
  flow: {
    copyO: hold(1),
    copyY: hold(0),
    face: { input: [0, 0.6], output: [STAGE.ink, STAGE.inkFlow] },
    oScale: { input: [0, 0.7], output: [0.8621, 0.9138] },
    oTurn: hold(0),
    liftO: hold(0),
    fadeO: hold(0),
  },
  static: {
    copyO: hold(1),
    copyY: hold(0),
    face: hold<string>(STAGE.ink),
    oScale: hold(0.8621),
    oTurn: hold(0),
    liftO: hold(0),
    fadeO: hold(0),
  },
};

function useTrack<T>(p: MotionValue<number>, { input, output, ease }: Track<T>) {
  return useTransform(p, input, output, { ease });
}

export default function HeroStage({ mode }: Props) {
  const sectionRef = useRef<HTMLElement>(null);

  const stageRef = useRef<HTMLDivElement>(null);

  const markRef = useRef<HTMLDivElement>(null);

  const ringPosRef = useRef<HTMLSpanElement>(null);

  const pinned = mode === 'pinned';

  const tracks = MAPS[mode];

  const { scrollYProgress: p } = useScroll({
    target: sectionRef,
    offset: pinned ? ['start start', 'end end'] : ['start start', 'end start'],
  });

  const partMax = useMotionValue(0);

  const dropMax = useMotionValue(0);

  const copyO = useTrack(p, tracks.copyO);
  const copyY = useTrack(p, tracks.copyY);
  const face = useTrack(p, tracks.face);
  const oScale = useTrack(p, tracks.oScale);
  const oTurn = useTrack(p, tracks.oTurn);
  const liftO = useTrack(p, tracks.liftO);
  const fadeO = useTrack(p, tracks.fadeO);

  const leftX = useTransform(
    [p, partMax],
    ([v, max]: number[]) => -max * EASE_FN.inOutCubic(segment(v, 0.2, 0.8))
  );

  const rightX = useTransform(leftX, (v) => -v);

  const markY = useTransform(
    [p, dropMax],
    ([v, max]: number[]) => max * EASE_FN.inOutCubic(segment(v, 0.1, 0.8))
  );

  const measure = useCallback(() => {
    const stage = stageRef.current;
    const ringPos = ringPosRef.current;
    const mark = markRef.current;
    if (!stage || !ringPos || !mark) {
      return;
    }
    const st = stage.getBoundingClientRect();
    const r = ringPos.getBoundingClientRect();
    partMax.set(
      partingFor(document.documentElement.clientWidth, mark.getBoundingClientRect().width)
    );
    dropMax.set(dropFor(st.height, r.top + r.height / 2 - st.top - markY.get()));
  }, [markY, partMax, dropMax]);

  useLayoutEffect(() => {
    if (!pinned) {
      return undefined;
    }

    let resize = 0;

    const onResize = () => {
      if (!resize) {
        resize = requestAnimationFrame(() => {
          resize = 0;
          measure();
        });
      }
    };

    measure();

    const observer = new ResizeObserver(onResize);

    if (markRef.current) {
      observer.observe(markRef.current);
    }

    window.addEventListener('resize', onResize, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', onResize);
      cancelAnimationFrame(resize);
    };
  }, [pinned, measure]);

  return (
    <Box
      ref={sectionRef}
      component="section"
      id={ANCHOR.top}
      aria-labelledby={HERO.titleId}
      sx={{
        position: 'relative',
        ...(pinned
          ? { height: '150vh', '@supports (height: 1svh)': { height: '150svh' } }
          : {
              minHeight: { xs: 'max(520px, 72vh)', sm: 'max(640px, 100vh)' },
              '@supports (height: 1svh)': {
                minHeight: { xs: 'max(520px, 72svh)', sm: 'max(640px, 100svh)' },
              },
            }),
        'html.hero-played & [data-enter]': { animation: 'none' },
        '&:focus-within [data-veil]': { opacity: '0 !important' },
        '&:focus-within [data-mark-move]': { transform: 'none !important' },
      }}
    >
      <Box
        ref={stageRef}
        sx={{
          ...(pinned
            ? {
                position: 'sticky',
                top: 0,
                height: '100vh',
                '@supports (height: 1svh)': { height: '100svh' },
              }
            : { position: 'relative', minHeight: 'inherit' }),
          overflow: 'hidden',
          display: 'grid',
          bgcolor: STAGE.ground,
        }}
      >
        <m.div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            background: STAGE.groundLift,
            willChange: pinned ? 'opacity' : undefined,
            opacity: liftO,
          }}
        />

        <Box
          sx={{
            ...CONTAINER,
            position: 'relative',
            zIndex: 1,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            pt: { xs: '88px', sm: '76px' },
            pb: { xs: '40px', sm: '48px' },
          }}
        >
          <Box
            sx={{
              containerType: 'inline-size',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <m.div
              data-mark-move
              style={{ willChange: pinned ? 'transform' : undefined, y: markY }}
            >
              <WordmarkDisplay
                mv={{ leftX, rightX, face, oScale, oTurn }}
                animated={mode !== 'static'}
                pinned={pinned}
                markRef={markRef}
                ringPosRef={ringPosRef}
              />
            </m.div>
          </Box>

          <m.div
            className="copy"
            data-hero-fade
            style={{
              position: 'relative',
              width: '100%',
              willChange: pinned ? 'transform, opacity' : undefined,
              opacity: copyO,
              y: copyY,
            }}
          >
            <TwoTone
              as="h1"
              id={HERO.titleId}
              lines={HERO.lines}
              size="hero"
              lineMode={{ xs: 'block', sm: 'inline' }}
              srPrefix={HERO.srPrefix}
              enter="load"
            />

            <Box
              component="p"
              data-enter
              sx={{
                ...TYPE.lead,
                maxWidth: { xs: '30ch', sm: '34em' },
                mx: 'auto',
                mt: { xs: '16px', sm: '20px' },
                mb: 0,
                color: STAGE.ink2,
                textWrap: 'balance',
                ...entrance(liftIn, { duration: 700, delay: 700 }),
              }}
            >
              <Rich parts={HERO.lead} />
            </Box>

            <Box
              data-enter
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'center',
                gap: { xs: '4px', sm: '28px' },
                maxWidth: { xs: 343, sm: 'none' },
                mx: 'auto',
                mt: { xs: '28px', sm: '36px' },
                ...entrance(liftIn, { duration: 700, delay: 820 }),
              }}
            >
              <ActionLink variant="pill" icon="down" href={HERO.primary.href}>
                {HERO.primary.label}
              </ActionLink>
              <ActionLink variant="link" icon="chevron" href={HERO.secondary.href}>
                {HERO.secondary.label}
              </ActionLink>
            </Box>
          </m.div>
        </Box>

        <m.div
          data-veil
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            background: STAGE.ground,
            pointerEvents: 'none',
            willChange: pinned ? 'opacity' : undefined,
            opacity: fadeO,
          }}
        />
      </Box>
    </Box>
  );
}
